import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { RadioGroupCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { closeDialogAction } from 'store/global/actions';

import { VerifyFactorType } from '../../DisclosureDelivery.constants';
import { DisclosureDeliveryTestIds } from '../../DisclosureDelivery.testIds';

const SSN_LAST4_PATTERN = /^\d{4}$/;
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;
const SSN_LAST4_MAX_LENGTH = 4;
const ZIP_MAX_LENGTH = 10;

const factorOptions = [
  { name: 'Last 4 of SSN', value: VerifyFactorType.SSN },
  { name: 'ZIP code', value: VerifyFactorType.ZIP },
];

const SwitchFactorDialog = ({ onSubmit }) => {
  const dispatch = useDispatch();

  const [type, setType] = useState(VerifyFactorType.SSN);
  const [factorValue, setFactorValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSsn = type === VerifyFactorType.SSN;
  const trimmedValue = factorValue.trim();

  const isValid = useMemo(
    () => (isSsn ? SSN_LAST4_PATTERN : ZIP_PATTERN).test(trimmedValue),
    [isSsn, trimmedValue],
  );

  const onChangeType = useCallback(event => {
    setType(event.target.value);
    setFactorValue('');
  }, []);

  const onChangeValue = useCallback(event => setFactorValue(event.target.value), []);

  const onClose = useCallback(() => dispatch(closeDialogAction()), [dispatch]);

  const onConfirm = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const succeeded = await onSubmit(
        isSsn
          ? { type: VerifyFactorType.SSN, ssnLast4: trimmedValue }
          : { type: VerifyFactorType.ZIP, zipCode: trimmedValue },
      );

      if (succeeded) {
        dispatch(closeDialogAction());
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, isSsn, onSubmit, trimmedValue]);

  return (
    <Box className="flex flex-col items-stretch gap-12">
      <Typography color="text.secondary" variant="regular14">
        Switch the identity factor the recipient must enter to open their disclosures. The existing
        signing link keeps working with the new factor.
      </Typography>
      <RadioGroupCDS
        dataTestId={DisclosureDeliveryTestIds.SWITCH_FACTOR_TYPE_RADIO}
        name="verify-factor-type"
        options={factorOptions}
        value={type}
        onChange={onChangeType}
      />
      <TextFieldCDS
        fullWidth
        dataTestId={DisclosureDeliveryTestIds.SWITCH_FACTOR_VALUE_INPUT}
        label={isSsn ? 'Last 4 of SSN' : 'ZIP code'}
        maxLength={isSsn ? SSN_LAST4_MAX_LENGTH : ZIP_MAX_LENGTH}
        name="verify-factor-value"
        value={factorValue}
        onChange={onChangeValue}
      />
      <Box className="pt-8 self-end flex gap-8">
        <Button
          data-testid={DisclosureDeliveryTestIds.SWITCH_FACTOR_CANCEL_BUTTON}
          disabled={isSubmitting}
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-testid={DisclosureDeliveryTestIds.SWITCH_FACTOR_SUBMIT_BUTTON}
          disabled={!isValid || isSubmitting}
          variant="contained"
          onClick={onConfirm}
        >
          Switch factor
        </Button>
      </Box>
    </Box>
  );
};

SwitchFactorDialog.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default SwitchFactorDialog;
